import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  InputSignal,
  model,
  ModelSignal,
  OnInit,
  Signal,
  signal,
  WritableSignal,
  inject,
} from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { TaxSummaryService } from '../../services/tax-summary.service';
import { TaxSummaryHeader } from '../../models/tax-summary-header';
import {
  combineLatest,
  map,
  Observable,
  of,
  switchMap,
  tap,
  timer,
} from 'rxjs';
import { TaxSummary } from '../../abstractions/tax-summary';
import { ActiveTaxSummary } from '../../abstractions/active-tax-summary';
import { CurrentUserService } from '@shared/services';
import { SidebarItemListService } from '@shared/services/sidebar-item.service';
import { InvestmentJurisdictionShort } from 'src/app/modules/admin/models/investment-jurisdiction-short';
import { parseTo } from '@shared/helpers/utils';

@Component({
  standalone: false,
  selector: 'gstar-tax-rates-summaries',
  templateUrl: './tax-rates-summaries.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TaxRatesSummariesComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly activatedRouter = inject(ActivatedRoute);
  private readonly taxSummaryService = inject(TaxSummaryService);
  private readonly sidebarService = inject(SidebarItemListService);
  private readonly currentUserService = inject(CurrentUserService);

  countryId: InputSignal<string> = input<string>('');

  active: ModelSignal<number> = model(0);
  tabs: WritableSignal<TaxSummaryHeader[]> = signal([]);
  countryName: WritableSignal<string> = signal('');
  showReadOnlyLabel: Signal<boolean> = computed(() => {
    const id = Number(this.countryId());
    return (
      this.currentUserService.isCountryAdmin() &&
      !this.currentUserService.canEditCountry(id)
    );
  });

  constructor() {
    this.sidebarService.selectedCountry$.subscribe(
      (country: InvestmentJurisdictionShort | null | undefined) => {
        if (country) {
          this.countryName.set(country.name);
        }
      }
    );
  }

  ngOnInit(): void {
    this.taxSummaryService
      .getTaxSummariesTypesHeaders()
      .pipe(
        switchMap((headers: TaxSummaryHeader[]) => {
          this.tabs.set(headers);
          return this.catchRouteParams();
        }),
        tap((activeTaxSummary: ActiveTaxSummary) =>
          this.taxSummaryService.activeTaxSummary$.next(activeTaxSummary)
        )
      )
      .subscribe(({ taxSummaryHeader }: ActiveTaxSummary): void => {
        const selectedIndex = this.tabs().findIndex(
          ({ taxSummaryHeaderId }: TaxSummaryHeader) => {
            return taxSummaryHeaderId === taxSummaryHeader.taxSummaryHeaderId;
          }
        );
        timer(0).subscribe(() => this.active.set(selectedIndex));
      });
  }

  tabChange(index: number): void {
    this.active.set(index);
    this.router.navigate([
      `/country/${this.countryId()}/tax-rates-summaries/${this.tabs().at(index)!.taxSummaryHeaderId}`,
    ]);
  }

  private catchRouteParams(): Observable<ActiveTaxSummary> {
    return combineLatest({
      country: this.activatedRouter.paramMap,
      taxSummaryType: this.getTaxSummmaryHeaderParams(),
    }).pipe(
      map(({ country, taxSummaryType }: TaxSummary): ActiveTaxSummary => {
        return {
          countryId: parseInt(country.get('countryId')!, 10),
          taxSummaryHeader: this.setTabIndexOrDefault(taxSummaryType),
        };
      })
    );
  }

  private getTaxSummmaryHeaderParams(): Observable<Params> {
    return this.activatedRouter.firstChild
      ? this.activatedRouter.firstChild.params
      : of(
          parseTo<Params>({
            taxSummaryTypeId: this.tabs().at(0)?.taxSummaryHeaderId,
          })
        );
  }

  private setTabIndexOrDefault(params: Params): TaxSummaryHeader {
    const taxSummaryHeader = this.tabs().find(
      (tab: TaxSummaryHeader) =>
        tab.taxSummaryHeaderId === parseInt(params['taxSummaryTypeId'], 10)
    );

    return !taxSummaryHeader ? this.tabs().at(0)! : taxSummaryHeader;
  }
}
